import {
  isLedgerSyncSyncActive,
  LEDGERSYNC_SYNC_PIPELINES,
  LEDGERSYNC_SYNC_STALL_MS,
  readLedgerSyncPipeline,
  type LedgerSyncSyncColumn,
  type LedgerSyncSyncPipeline,
} from '@dcp/shared/ledgersync/sync-state'
import type { BankConnectionRow } from '@/lib/db-tenant'

/**
 * How the connection screens read a row's two LedgerSync sync pipelines
 * (DCP-401). One place, so the list, the detail header, the poll and the
 * completion watcher cannot disagree about whether a connection is syncing.
 *
 * Every "is it running" answer goes through `isLedgerSyncSyncActive` from the
 * shared contract, which is stall-aware: a non-terminal status older than the
 * stall ceiling is a crashed/cancelled run and reads as NOT running. Never
 * test `sync_status === 'syncing'` directly.
 */

/** The six status columns `get_bank_connections` returns per connection. */
export type ConnectionSyncState = Pick<BankConnectionRow, LedgerSyncSyncColumn>

/** A pipeline whose latest terminal state is `failed`. */
export interface ConnectionSyncFailure {
  pipeline: LedgerSyncSyncPipeline
  /** The raw `*_error`; null when the run failed without recording why. */
  message: string | null
}

/** Everything the UI shows about a connection's syncs, derived from one row. */
export interface ConnectionSyncActivity {
  /** The transactions pipeline is genuinely in flight. */
  transactionsActive: boolean
  /** The balances pipeline is genuinely in flight. */
  balancesActive: boolean
  /** Either pipeline is in flight — what disables that connection's Sync. */
  active: boolean
  /** Short phase label while active (e.g. "Waiting for institution…"); null when idle. */
  label: string | null
  /** Pipelines whose latest outcome is `failed`; empty when none. */
  failures: ConnectionSyncFailure[]
}

/**
 * Phase labels of the TRANSACTIONS pipeline. It is the one with phases worth
 * naming — `refreshing` can last many minutes, and "Waiting for institution…" is
 * what tells the user the delay is the institution's, not ours.
 */
const TRANSACTION_PHASE_LABEL: Record<string, string> = {
  queued: 'Queued…',
  refreshing: 'Waiting for institution…',
  syncing: 'Syncing…',
}

/** Shown when only the balances pipeline is running. */
const BALANCES_ONLY_LABEL = 'Syncing balances…'

/**
 * Is one pipeline of `row` in flight?
 *
 * @param row - A connection row
 * @param pipeline - Which pipeline to ask about
 * @param now - Epoch ms to age the status against
 */
export function isPipelineSyncActive(
  row: ConnectionSyncState,
  pipeline: LedgerSyncSyncPipeline,
  now: number = Date.now(),
): boolean {
  const { status, statusAt } = readLedgerSyncPipeline(row, pipeline)
  return isLedgerSyncSyncActive(status, statusAt, now)
}

/**
 * Is the transactions pipeline of `row` in flight?
 *
 * @param row - A connection row
 * @param now - Epoch ms to age the status against
 */
export function isTransactionSyncActive(row: ConnectionSyncState, now: number = Date.now()): boolean {
  return isPipelineSyncActive(row, 'transactions', now)
}

/**
 * Is the balances pipeline of `row` in flight?
 *
 * @param row - A connection row
 * @param now - Epoch ms to age the status against
 */
export function isBalanceSyncActive(row: ConnectionSyncState, now: number = Date.now()): boolean {
  return isPipelineSyncActive(row, 'balances', now)
}

/**
 * Does any row have either pipeline in flight? The connection list's poll
 * condition.
 *
 * @param rows - The connection list; `undefined` before the first response
 * @param now - Epoch ms to age the statuses against
 */
export function anyConnectionSyncActive(
  rows: readonly ConnectionSyncState[] | undefined,
  now: number = Date.now(),
): boolean {
  return (rows ?? []).some((row) =>
    LEDGERSYNC_SYNC_PIPELINES.some((pipeline) => isPipelineSyncActive(row, pipeline, now)),
  )
}

/**
 * The earliest moment at which a pipeline that is active NOW stops being
 * active purely by ageing — i.e. `*_status_at + LEDGERSYNC_SYNC_STALL_MS` of
 * the soonest-to-stall active pipeline across `rows`.
 *
 * WHY THE SCREENS NEED IT. A run that crashed or was cancelled never writes a
 * terminal status, so its row turns "not running" at exactly this instant
 * with NO data change: the poll stops (its predicate is re-evaluated), but the
 * refetch returns a structurally equal list, React Query keeps the same data
 * reference and nothing re-renders — the spinner and the disabled Sync would
 * outlive the run for the rest of the session. The screens schedule one
 * re-render at this deadline (`useSyncStallRerender`).
 *
 * Only ACTIVE pipelines count (via the shared stall-aware predicate, so the
 * rule is not restated here): an already-stale status has no future deadline.
 *
 * @param rows - The connection list; `undefined` before the first response
 * @param now - Epoch ms to age the statuses against
 * @returns Epoch ms of the soonest deadline, or null when nothing is active
 */
export function nextSyncStallDeadline(
  rows: readonly ConnectionSyncState[] | undefined,
  now: number = Date.now(),
): number | null {
  let soonest: number | null = null
  for (const row of rows ?? []) {
    for (const pipeline of LEDGERSYNC_SYNC_PIPELINES) {
      const { status, statusAt } = readLedgerSyncPipeline(row, pipeline)
      if (!statusAt || !isLedgerSyncSyncActive(status, statusAt, now)) continue
      const deadline = Date.parse(statusAt) + LEDGERSYNC_SYNC_STALL_MS
      if (soonest === null || deadline < soonest) soonest = deadline
    }
  }
  return soonest
}

/**
 * Derive the sync presentation of one connection.
 *
 * The label prefers the transactions pipeline's phase: a per-connection Sync
 * runs both pipelines off the same bank refresh, and the transactions side is
 * the one whose phases (queued / waiting for bank / syncing) explain the wait.
 * Only when balances run alone ("Sync all balances") is the generic balances
 * label shown.
 *
 * @param row - A connection row
 * @param now - Epoch ms to age the statuses against
 */
export function getConnectionSyncActivity(
  row: ConnectionSyncState,
  now: number = Date.now(),
): ConnectionSyncActivity {
  const transactionsActive = isTransactionSyncActive(row, now)
  const balancesActive = isBalanceSyncActive(row, now)
  let label: string | null = null
  if (transactionsActive) {
    label = TRANSACTION_PHASE_LABEL[readLedgerSyncPipeline(row, 'transactions').status ?? ''] ?? 'Syncing…'
  }
  else if (balancesActive) label = BALANCES_ONLY_LABEL

  const failures: ConnectionSyncFailure[] = []
  for (const pipeline of LEDGERSYNC_SYNC_PIPELINES) {
    const { status, error } = readLedgerSyncPipeline(row, pipeline)
    if (status === 'failed') failures.push({ pipeline, message: error })
  }

  return {
    transactionsActive,
    balancesActive,
    active: transactionsActive || balancesActive,
    label,
    failures,
  }
}

/**
 * One line per failed pipeline, for a tooltip: "Transactions sync failed: …".
 *
 * @param failures - From {@link getConnectionSyncActivity}
 */
export function describeSyncFailures(failures: readonly ConnectionSyncFailure[]): string {
  return failures
    .map(({ pipeline, message }) => {
      const what = pipeline === 'transactions' ? 'Transactions sync failed' : 'Balance sync failed'
      return message ? `${what}: ${message}` : what
    })
    .join('\n')
}
